import React, { useEffect, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { getUser } from "../../redux/actions/users";
import { RevAlert, RevButton, RevCard, RevTipRow } from "../../components/ds";

const TABS = ["photographers", "participants", "emails"];

const Guide = ({ match, user, getUser }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(null);

    // Única ruta privada que no pedía GET /user: sin él, el raíl y la cabecera se quedaban
    // sin saldo, sin iniciales y sin nombre mientras se estuviera aquí (y la firma de los
    // correos caía al texto sin nombre). Se pide al montar, que es ya después de resolver la
    // auth: desde Nav no valdría, porque ahora monta ANTES y el GET saldría sin token.
    useEffect(() => {
        if (!user?.id) getUser();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!copied) return undefined;
        const timeoutId = setTimeout(() => setCopied(null), 3000);
        return () => clearTimeout(timeoutId);
    }, [copied]);

    const tab = TABS.includes(match?.params?.tab) ? match.params.tab : TABS[0];

    const name = user?.name?.trim();
    const signoff = name
        ? t('guide.emails.signoff', { name })
        : t('guide.emails.signoffNoName');

    const templates = [
        {
            id: 'main',
            label: t('guide.emails.main.label'),
            subject: t('guide.emails.main.subject'),
            body: t('guide.emails.main.body'),
            note: t('guide.emails.main.note'),
        },
        {
            id: 'reminder',
            label: t('guide.emails.reminder.label'),
            subject: t('guide.emails.reminder.subject'),
            body: t('guide.emails.reminder.body'),
            note: t('guide.emails.reminder.note'),
        },
    ];

    const fullBody = (template) => `${template.body}\n\n${signoff}`;

    const onCopy = (template) => {
        const text = `${template.subject}\n\n${fullBody(template)}`;
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).catch(() => { });
        }
        setCopied(template.id);
    };

    const tabClass = (value) => `rev-tabs__tab ${tab === value ? 'is-active' : ''}`;
    const tabCurrent = (value) => (tab === value ? 'page' : undefined);

    return (
        <div className="rev-pagina">
            <div className="rev-guia-head">
                <div className="rev-page-titles">
                    <h1 className="rev-page-title">{t('guide.title')}</h1>
                    <span className="rev-page-subtitle">{t('guide.subtitle')}</span>
                </div>
                <nav className="rev-tabs" aria-label={t('guide.tabs.label')}>
                    <Link to="/guide/photographers" className={tabClass('photographers')} aria-current={tabCurrent('photographers')}>
                        {t('guide.tabs.photographers')}
                    </Link>
                    <Link to="/guide/participants" className={tabClass('participants')} aria-current={tabCurrent('participants')}>
                        {t('guide.tabs.participants')}
                    </Link>
                    <Link to="/guide/emails" className={tabClass('emails')} aria-current={tabCurrent('emails')}>
                        {t('guide.tabs.emails')}
                    </Link>
                </nav>
            </div>

            {tab === 'photographers' &&
                <div className="rev-cols rev-cols--flush">
                    <div className="rev-cols__main">
                        <RevCard title={t('guide.camera.title')} sub={t('guide.camera.sub')}>
                            <RevTipRow ruled n="01" title={t('guide.camera.r1')} />
                            <RevTipRow ruled n="02" title={t('guide.camera.r2')} />
                            <RevTipRow ruled n="03" title={t('guide.camera.r3')} />
                            <RevTipRow ruled n="04" title={t('guide.camera.r4')} />
                        </RevCard>
                        <RevCard title={t('guide.upload.title')} sub={t('guide.upload.sub')}>
                            <RevTipRow ruled n="01" title={t('guide.upload.r1')} />
                            <RevTipRow ruled n="02" title={t('guide.upload.r2')} />
                            <RevTipRow ruled n="03" title={t('guide.upload.r3')} />
                            <RevTipRow ruled n="04" title={t('guide.upload.r4')} />
                            <RevTipRow ruled n="05" title={t('guide.upload.r5')} />
                        </RevCard>
                    </div>
                    <div className="rev-cols__aside">
                        <RevCard
                            title={t('guide.workflow.title')}
                            foot={<span className="rev-help">{t('guide.workflow.foot')}</span>}>
                            <RevTipRow n="01" title={t('event.new.steps.s1.title')} detail={t('event.new.steps.s1.detail')} />
                            <RevTipRow n="02" title={t('event.new.steps.s2.title')} detail={t('event.new.steps.s2.detail')} />
                            <RevTipRow n="03" title={t('event.new.steps.s3.title')} detail={t('event.new.steps.s3.detail')} />
                        </RevCard>
                        <RevCard title={t('guide.errors.title')}>
                            <span className="rev-help">{t('guide.errors.e1')}</span>
                            <span className="rev-help rev-help--divider">{t('guide.errors.e2')}</span>
                            <span className="rev-help rev-help--divider">{t('guide.errors.e3')}</span>
                        </RevCard>
                    </div>
                </div>}

            {tab === 'participants' &&
                <div className="rev-cols rev-cols--flush">
                    <div className="rev-cols__main">
                        <RevCard title={t('guide.find.title')} sub={t('guide.find.sub')}>
                            <RevTipRow ruled n="01" title={t('guide.find.s1.title')} detail={t('guide.find.s1.detail')} />
                            <RevTipRow ruled n="02" title={t('guide.find.s2.title')} detail={t('guide.find.s2.detail')} />
                            <RevTipRow ruled n="03" title={t('guide.find.s3.title')} detail={t('guide.find.s3.detail')} />
                            <RevTipRow ruled n="04" title={t('guide.find.s4.title')} detail={t('guide.find.s4.detail')} />
                        </RevCard>
                    </div>
                    <div className="rev-cols__aside">
                        <RevCard title={t('guide.faq.title')}>
                            <div className="rev-guia-faq">
                                <span className="rev-label">{t('guide.faq.q1')}</span>
                                <span className="rev-help">{t('guide.faq.a1')}</span>
                            </div>
                            <div className="rev-guia-faq">
                                <span className="rev-label">{t('guide.faq.q2')}</span>
                                <span className="rev-help">{t('guide.faq.a2')}</span>
                            </div>
                            <div className="rev-guia-faq">
                                <span className="rev-label">{t('guide.faq.q3')}</span>
                                <span className="rev-help">{t('guide.faq.a3')}</span>
                            </div>
                        </RevCard>
                    </div>
                </div>}

            {tab === 'emails' &&
                <div className="rev-guia-emails">
                    <RevAlert tone="warn" title={t('guide.emails.warn.title')}>
                        <span className="rev-help">{t('guide.emails.warn.body')}</span>
                    </RevAlert>
                    <RevAlert tone="info" title={t('guide.emails.info.title')}>
                        <span className="rev-help">{t('guide.emails.info.body')}</span>
                    </RevAlert>
                    {templates.map(template =>
                        <RevCard
                            key={template.id}
                            title={template.label}
                            foot={<>
                                <span className="rev-help">{template.note}</span>
                                <div className="rev-card__foot-actions">
                                    <RevButton variant="secondary" onClick={() => onCopy(template)}>
                                        {copied === template.id
                                            ? <><span className="material-icons rev-icon-sm">check</span>{t('guide.emails.copied')}</>
                                            : t('guide.emails.copy')}
                                    </RevButton>
                                </div>
                            </>}>
                            <div className="rev-guia-email__group">
                                <span className="rev-label">{t('guide.emails.subject')}</span>
                                <div className="rev-guia-email__box">{template.subject}</div>
                            </div>
                            <div className="rev-guia-email__group">
                                <span className="rev-label">{t('guide.emails.body')}</span>
                                <div className="rev-guia-email__box rev-guia-email__box--body">{fullBody(template)}</div>
                            </div>
                        </RevCard>)}
                </div>}
        </div>
    );
};

const mapStateToProps = state => (
    { user: state.usersReducer.user }
);

const mapDispatchToProps = {
    getUser
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Guide));
